import type { TFunction } from "i18next";
import { formatDateTime } from "../../../lib/datetime/displayDateTime";

import Chip from "@mui/material/Chip";
import { formatCustomerUsers } from "../../../lib/customer/users";
import { formatCustomerPhone } from "../../../lib/customer/phone";
import { optionLabel } from "../../../lib/i18n/optionLabel";

export enum CustomerStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  SUSPENDED = "SUSPENDED",
}

export const getCustomerCols = (t: TFunction) => [
  {
    name: t("customers.cols.name"),
    selector: (row: any) => row.name,
    sortable: true,
  },
  {
    name: t("customers.cols.users"),
    selector: (row: any) => formatCustomerUsers(row.users ?? row.username),
    sortable: true,
  },
  {
    name: t("customers.cols.email"),
    selector: (row: any) => row.email,
    sortable: true,
  },
  {
    name: t("customers.cols.phone"),
    selector: (row: any) => formatCustomerPhone(row.phone),
    sortable: true,
  },
  {
    name: t("customers.cols.mobilePhone"),
    selector: (row: any) => formatCustomerPhone(row.mobilePhone),
    sortable: true,
  },
  {
    name: t("customers.cols.pointOfContact"),
    selector: (row: any) => row.pointOfContact,
    sortable: true,
  },
  {
    name: t("customers.cols.status"),
    selector: (row: any) => row.status,
    sortable: true,
    cell: (row: any) => {
      const status = row.status as string | undefined;
      const color =
        status === "ACTIVE"
          ? ("success" as const)
          : status === "INACTIVE"
            ? ("error" as const)
            : status === "SUSPENDED"
              ? ("warning" as const)
              : ("default" as const);
      return (
        <Chip
          label={optionLabel(t, "options.customerStatus", status)}
          size="small"
          color={color}
          variant={color === "default" ? "outlined" : "filled"}
        />
      );
    },
  },
  {
    name: t("customers.cols.workTime"),
    selector: (row: any) =>
      row.startWorkTime && row.endWorkTime ? `${row.startWorkTime} - ${row.endWorkTime}` : t("common.na"),
    sortable: false,
  },
  {
    name: t("customers.cols.createdDate"),
    selector: (row: any) =>
      row.createdDate ? formatDateTime(row.createdDate) : t("common.na"),
    sortable: true,
  },
];

const CustomerModel = {
  get cols() {
    return getCustomerCols(((key: string) => key) as TFunction);
  },
  createDialogOpts: (t: TFunction) => ({
    submitButtonText: t("common.create"),
    cancelButtonText: t("common.cancel"),
    title: t("customers.createTitle"),
    content: t("customers.createContent"),
  }),
  editDialogOpts: (t: TFunction) => ({
    submitButtonText: t("common.update"),
    cancelButtonText: t("common.cancel"),
    title: t("customers.editTitle"),
    content: t("customers.editContent"),
  }),
  WARNING: {
    color: "warning",
    icon: "warning",
    title: "",
    content: "",
    info: "",
  },
  ERROR: {
    color: "error",
    icon: "error",
    title: "",
    content: "",
    info: "",
  },
  SUCCESS: {
    color: "success",
    icon: "success",
    title: "",
    content: "",
    info: "",
  },
  async message(type: any, title: any, content: any, info: any) {
    return new Promise((resolve) => {
      type.title = title;
      type.content = content;
      type.info = info;
      resolve(type);
    });
  },
};

export default CustomerModel;
